import { audience, triggers } from '@bb/contracts';

export type ActionBasedDelivery = triggers.ActionBasedDelivery;
export type TriggerSpec = triggers.TriggerSpec;
export type TriggerKind = TriggerSpec['kind'];
export type TriggerDelay = triggers.TriggerDelay;
export type DelayKind = TriggerDelay['kind'];
export type TimeFrame = triggers.TimeFrame;
export type PropertyCondition = triggers.PropertyCondition;
export type ConditionType = PropertyCondition['dataType'];
export type AttributeTarget = Extract<TriggerSpec, { kind: 'attribute_changed' }>['to'];

/** Limits of `triggers.ActionBasedDelivery`: triggers (OR), cancel events and property filters per custom event. */
export const MAX_TRIGGERS = 10;
export const MAX_CANCEL_EVENTS = 10;
export const MAX_PROPERTY_FILTERS = 10;
export const DEFAULT_SEND_TIME = '10:00';

/** A fresh trigger of a kind: the fields the user still has to choose are empty and reported until chosen. */
export function triggerTemplate(kind: TriggerKind): TriggerSpec {
  switch (kind) {
    case 'custom_event':
      return { kind, event: '', propertyFilters: [] };
    case 'session_start':
      return { kind };
    case 'attribute_changed':
      return { kind, attribute: '', to: { kind: 'any' } };
    case 'subscription_changed':
      return { kind, groupId: '', to: 'subscribed' };
    case 'message_interaction':
      return { kind, messageApiId: '', interaction: 'click' };
  }
}

export const DEFAULT_TIME_FRAME: TimeFrame = { from: '09:00', to: '21:00', localTime: false, sendAtNextAvailable: true };

/** A new action-based draft: one custom event to choose, sent immediately. */
export const emptyActionBased = (): ActionBasedDelivery => ({ triggers: [triggerTemplate('custom_event')], delay: { kind: 'none' }, cancelOn: [], reevaluateAudienceAtSend: false });

function triggerWithDefaults(spec: TriggerSpec): TriggerSpec {
  if (spec.kind === 'custom_event') return { ...spec, propertyFilters: spec.propertyFilters ?? [] };
  if (spec.kind === 'attribute_changed') return { ...spec, to: spec.to ?? { kind: 'any' } };
  return spec;
}

/**
 * The value as the form edits it: a stored draft may hold the contract's input shape (defaults left out), so the
 * defaults are filled in; nothing else changes.
 */
export function withDefaults(value: ActionBasedDelivery | undefined): ActionBasedDelivery {
  if (!value) return emptyActionBased();
  const v = value as Partial<ActionBasedDelivery>;
  const delay = v.delay ?? { kind: 'none' };
  const next: ActionBasedDelivery = {
    ...value,
    triggers: (v.triggers ?? []).map(triggerWithDefaults),
    delay: delay.kind === 'next_weekday' || delay.kind === 'calendar_days' ? { ...delay, localTime: delay.localTime ?? false } : delay,
    cancelOn: (v.cancelOn ?? []).map(triggerWithDefaults),
    reevaluateAudienceAtSend: v.reevaluateAudienceAtSend ?? false,
  };
  if (v.timeFrame) next.timeFrame = { ...v.timeFrame, localTime: v.timeFrame.localTime ?? false, sendAtNextAvailable: v.timeFrame.sendAtNextAvailable ?? true };
  return next;
}

// ---------------------------------------------------------------------------------------------------------------
// Delay

export type DelayUnit = 'minutes' | 'hours' | 'days';
export const UNIT_SECONDS: Record<DelayUnit, number> = { minutes: 60, hours: 3_600, days: 86_400 };

/** The largest unit that divides a delay, so a stored delay reads as it was entered (`7200` → 2 hours). */
export function unitOf(seconds: number): DelayUnit {
  if (seconds > 0 && seconds % UNIT_SECONDS.days === 0) return 'days';
  if (seconds > 0 && seconds % UNIT_SECONDS.hours === 0) return 'hours';
  return 'minutes';
}

export const toSeconds = (amount: number, unit: DelayUnit): number => Math.round(amount * UNIT_SECONDS[unit]);

/** The delay for another kind: the wall time and local time carry over between weekday and calendar-day delays. */
export function switchDelayKind(delay: TriggerDelay, kind: DelayKind): TriggerDelay {
  if (delay.kind === kind) return delay;
  const time = 'time' in delay ? delay.time : DEFAULT_SEND_TIME;
  const localTime = 'localTime' in delay ? delay.localTime : false;
  switch (kind) {
    case 'none':
      return { kind };
    case 'after':
      return { kind, seconds: UNIT_SECONDS.hours };
    case 'next_weekday':
      return { kind, weekday: 1, time, localTime };
    case 'calendar_days':
      return { kind, days: 1, time, localTime };
  }
}

/** A time frame whose end is earlier than its start runs over midnight (22:00–06:00). */
export const crossesMidnight = (frame: TimeFrame): boolean => frame.from > frame.to;

// ---------------------------------------------------------------------------------------------------------------
// Property conditions (the operators of segment filters, BB-40/BB-42)

const COMPARABLE: readonly ConditionType[] = ['string', 'number', 'boolean', 'array'];

/** How a registry property compares; object and null properties cannot be filtered on. */
export const conditionTypeOf = (registryType: string): ConditionType | null => (COMPARABLE as readonly string[]).includes(registryType) ? (registryType as ConditionType) : null;

export const operatorsFor = (dataType: ConditionType): readonly string[] => audience.OPERATORS_BY_TYPE[dataType] ?? [];

const NO_VALUE = new Set(['exists', 'not_exists', 'is_true', 'is_false']);
const DAY_COUNT = new Set(['more_than_days_ago', 'less_than_days_ago', 'within_next_days']);
const SIZE = new Set(['size_equals', 'size_gt', 'size_lt']);

/** What the operand of a condition looks like, which decides its inputs. */
export type OperandKind = 'none' | 'number' | 'days' | 'text' | 'list' | 'date' | 'range';
export function operandKind(dataType: ConditionType, operator: string): OperandKind {
  if (NO_VALUE.has(operator)) return 'none';
  if (operator === 'between') return 'range';
  if (operator === 'is_any_of') return 'list';
  if (DAY_COUNT.has(operator)) return 'days';
  if (dataType === 'number' || SIZE.has(operator)) return 'number';
  if (dataType === 'time') return 'date';
  return 'text';
}

const DEFAULT_OPERATOR: Record<ConditionType, string> = { string: 'equals', number: 'equals', boolean: 'is_true', time: 'after', array: 'includes_value' };

/** An operator change keeps an operand of the same shape and resets any other, so no stale value is read differently. */
export function withConditionOperator(condition: PropertyCondition, operator: string): PropertyCondition {
  const before = operandKind(condition.dataType, condition.operator);
  const after = operandKind(condition.dataType, operator);
  if (before === after) return { ...condition, operator };
  const { value: _value, value2: _value2, ...rest } = condition;
  switch (after) {
    case 'text':
    case 'date':
      return { ...rest, operator, value: '' };
    case 'list':
      return { ...rest, operator, value: [] };
    default:
      return { ...rest, operator };
  }
}

/** A condition on a property, with the default operator of its type. */
export function newCondition(path: string, dataType: ConditionType): PropertyCondition {
  return withConditionOperator({ path, dataType, operator: 'exists' }, DEFAULT_OPERATOR[dataType]);
}

/** A condition with no property yet: the user picks it first. */
export const emptyCondition = (): PropertyCondition => ({ path: '', dataType: 'string', operator: 'equals', value: '' });

const DATE_OPERAND = /^\d{4}-\d{2}-\d{2}$/;
const TIME_OPERAND = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isDate = (v: unknown, withTime: boolean) => typeof v === 'string' && (DATE_OPERAND.test(v) || (withTime && TIME_OPERAND.test(v))) && !Number.isNaN(Date.parse(v));
const validRegex = (pattern: string) => {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
};

/**
 * The operand rules of segment comparisons (`checkComparison` of the audience engine), which the contract leaves open:
 * without them `score >= (nothing)` would be saved and never match.
 */
export function conditionIssue(condition: PropertyCondition): { field: 'operator' | 'value' | 'value2'; message: string } | null {
  const { dataType, operator, value } = condition;
  if (!operatorsFor(dataType).includes(operator)) return { field: 'operator', message: 'Choose an operator.' };
  switch (operandKind(dataType, operator)) {
    case 'none':
      return value === undefined ? null : { field: 'value', message: 'This operator takes no value.' };
    case 'list':
      return Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === 'string') ? null : { field: 'value', message: 'Enter at least one value.' };
    case 'range':
      if (!isFiniteNumber(value)) return { field: 'value', message: 'Enter a number.' };
      if (!isFiniteNumber(condition.value2)) return { field: 'value2', message: 'Enter a number.' };
      return value <= condition.value2 ? null : { field: 'value2', message: 'Enter a number not below the first one.' };
    case 'number':
      if (SIZE.has(operator)) return Number.isInteger(value) && (value as number) >= 0 ? null : { field: 'value', message: 'Enter a whole number.' };
      return isFiniteNumber(value) ? null : { field: 'value', message: 'Enter a number.' };
    case 'days':
      return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 3650 ? null : { field: 'value', message: 'Enter a whole number of days from 0 to 3650.' };
    case 'date':
      return isDate(value, operator !== 'on') ? null : { field: 'value', message: 'Enter a date.' };
    case 'text':
      if (typeof value !== 'string') return { field: 'value', message: 'Enter a value.' };
      if ((operator === 'matches_regex' || operator === 'not_matches_regex') && !validRegex(value)) return { field: 'value', message: 'Enter a valid regular expression.' };
      return value === '' && (operator === 'contains' || operator === 'not_contains' || operator === 'starts_with') ? { field: 'value', message: 'Enter a value.' } : null;
  }
}

/**
 * Issues of one custom event outside an action-based value — a funnel step or return event (BB-135), a card removal
 * event (BB-65): `path` + `event`, and `path` + `propertyFilters.<j>.<field>` for the operand rules, as `CustomEventFields`
 * shows them.
 */
export function customEventIssues(path: Array<string | number>, event: string, filters: readonly PropertyCondition[]): Array<{ path: Array<string | number>; message: string }> {
  const out: Array<{ path: Array<string | number>; message: string }> = [];
  if (!event.trim()) out.push({ path: [...path, 'event'], message: 'Choose an event.' });
  filters.forEach((f, j) => {
    if (!f.path) {
      out.push({ path: [...path, 'propertyFilters', j, 'path'], message: 'Choose a property.' });
      return;
    }
    const issue = conditionIssue(f);
    if (issue) out.push({ path: [...path, 'propertyFilters', j, issue.field], message: issue.message });
  });
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Validation

export interface ActionBasedIssue {
  path: Array<string | number>;
  message: string;
}

export type ActionBasedValidation = { ok: true; value: ActionBasedDelivery; issues: [] } | { ok: false; issues: ActionBasedIssue[] };

const FIELD_MESSAGES: Record<string, string> = {
  kind: 'Choose what the user does.',
  event: 'Choose an event.',
  attribute: 'Choose an attribute.',
  groupId: 'Choose a subscription group.',
  messageApiId: 'Choose a campaign.',
  appInstanceIds: 'Choose apps from the list.',
  path: 'Choose a property.',
  seconds: 'Enter a delay from 1 minute to 90 days.',
  days: 'Enter from 1 to 90 days.',
  weekday: 'Choose a weekday.',
  time: 'Enter a time.',
};

type ZodIssue = { path: PropertyKey[]; code: string; message: string };

function messageOf(issue: ZodIssue, path: Array<string | number>): string {
  const last = path[path.length - 1];
  const list = path.length === 1 ? last : path.length === 3 && last === 'propertyFilters' ? last : undefined;
  if (list && issue.code === 'too_small' && last === 'triggers') return 'Add at least one trigger.';
  if (list && issue.code === 'too_big') return last === 'triggers' ? `Use at most ${MAX_TRIGGERS} triggers.` : last === 'cancelOn' ? `Use at most ${MAX_CANCEL_EVENTS} cancel events.` : `Use at most ${MAX_PROPERTY_FILTERS} property filters.`;
  if (path[0] === 'timeFrame' && (last === 'from' || last === 'to')) return 'Enter a time.';
  const key = [...path].reverse().find((p): p is string => typeof p === 'string');
  if (key === 'value' && path.includes('to')) return 'Enter the new value.';
  return (key && FIELD_MESSAGES[key]) ?? issue.message.charAt(0).toUpperCase() + issue.message.slice(1);
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Operand issues of every property condition that the contract accepted as a shape. */
function conditionIssues(value: unknown, reported: ActionBasedIssue[]): ActionBasedIssue[] {
  const out: ActionBasedIssue[] = [];
  const root = (value ?? {}) as Record<string, unknown>;
  for (const list of ['triggers', 'cancelOn'] as const) {
    asArray(root[list]).forEach((spec, i) => {
      const filters = (spec as { kind?: unknown; propertyFilters?: unknown })?.kind === 'custom_event' ? asArray((spec as { propertyFilters?: unknown }).propertyFilters) : [];
      filters.forEach((raw, j) => {
        const parsed = triggers.PropertyCondition.safeParse(raw);
        const at = [list, i, 'propertyFilters', j];
        if (!parsed.success || reported.some((r) => at.every((p, k) => r.path[k] === p))) return;
        const issue = conditionIssue(parsed.data);
        if (issue) out.push({ path: [...at, issue.field], message: issue.message });
      });
    });
  }
  return out;
}

/**
 * Checks an action-based delivery with the server's schema (`triggers.ActionBasedDelivery`) and the operand rules of
 * its property conditions; every issue has the path of the field it belongs to and a message for the form.
 */
export function validateActionBased(value: unknown): ActionBasedValidation {
  const parsed = triggers.ActionBasedDelivery.safeParse(value);
  const issues: ActionBasedIssue[] = parsed.success
    ? []
    : parsed.error.issues.map((issue) => {
        const path = issue.path.filter((p): p is string | number => typeof p !== 'symbol');
        return { path, message: messageOf(issue, path) };
      });
  issues.push(...conditionIssues(value, issues));
  if (parsed.success && issues.length === 0) return { ok: true, value: parsed.data, issues: [] };
  return { ok: false, issues };
}

/** Messages of the issues at exactly this path. */
export const issuesAt = (issues: readonly ActionBasedIssue[], ...path: Array<string | number>): string[] =>
  issues.filter((i) => i.path.length === path.length && path.every((p, k) => i.path[k] === p)).map((i) => i.message);

/** Messages of the issues at this path or below it (e.g. one bad id inside a list). */
export const issuesUnder = (issues: readonly ActionBasedIssue[], ...path: Array<string | number>): string[] =>
  issues.filter((i) => i.path.length >= path.length && path.every((p, k) => i.path[k] === p)).map((i) => i.message);

/** «Changes to a specific value»; no value yet while a number is being entered (the contract reports it). */
export const equalsTo = (value: string | number | boolean | undefined): AttributeTarget => (value === undefined ? { kind: 'equals' } : { kind: 'equals', value }) as AttributeTarget;
